import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatMinorUnits, formatPercent, formatQuantity } from '../reportFormatters';
import { resolveCatalogLabel, resolveProductReportSection } from '../reportCatalogLabels';
import { productSectionColor } from '../reportChartPalette';
import { CommercialValue } from './CommercialValue';

const SORTABLE_COLUMNS = {
  name: 'components.pages.reports.products.product',
  fulfilled_quantity: 'components.pages.reports.products.quantity',
  orders: 'components.pages.reports.products.orders',
  net_sales_without_vat: 'components.pages.reports.products.netWithoutVat',
  net_sales_with_vat: 'components.pages.reports.products.netWithVat'
};

function ProductSortButton({ column, table, onChange }) {
  const intl = useIntl();
  const isActive = table.sortBy === column;
  const nextDirection = isActive && table.sortDirection === 'desc' ? 'asc' : 'desc';
  const columnLabel = intl.formatMessage({ id: SORTABLE_COLUMNS[column] });
  const scopeLabel = intl.formatMessage({ id: 'components.pages.reports.products.sortWithinGroups' });

  return (
    <button
      aria-label={`${scopeLabel}: ${columnLabel}`}
      aria-pressed={isActive}
      className="report-products__sort"
      onClick={() => onChange({ ...table, page: 1, sortBy: column, sortDirection: nextDirection })}
      title={`${scopeLabel}: ${columnLabel}`}
      type="button"
    >
      <FormattedMessage id={SORTABLE_COLUMNS[column]}/>
      <i
        aria-hidden="true"
        className={`fas ${isActive && table.sortDirection === 'asc' ? 'fa-sort-up' : isActive ? 'fa-sort-down' : 'fa-sort'}`}
      />
    </button>
  );
}

function ProductRow({ row, labels }) {
  const intl = useIntl();
  const quantity = row.fulfilled_quantity || {};
  const orders = row.orders || {};
  const comparison = row.comparison && row.comparison.changes;
  const directAllocationOnly = Number(orders.billable || 0) === 0
    && Number(orders.direct_allocation || 0) > 0;
  const productLabel = resolveCatalogLabel(row, intl.locale, intl.formatMessage) || '—';

  return (
    <tr data-testid={`product-report-row-${row.uuid || row.id}`}>
      <td data-label={labels.product}>
        <strong className="report-products__product-name">{productLabel}</strong>
        {directAllocationOnly && (
          <span className="report-products__allocation-badge">
            <FormattedMessage id="components.pages.reports.products.directAllocationOnly"/>
          </span>
        )}
      </td>
      <td data-label={labels.quantity}>
        <strong>{formatQuantity(quantity.net)}</strong>
        <span className="report-products__details">
          <FormattedMessage id="components.pages.reports.products.purchased"/>: {formatQuantity(quantity.purchased)}
          {' · '}<FormattedMessage id="components.pages.reports.products.direct"/>: {formatQuantity(quantity.direct_allocated)}
          {' · '}<FormattedMessage id="components.pages.reports.products.refunded"/>: {formatQuantity(quantity.refunded)}
        </span>
      </td>
      <td data-label={labels.orders}>
        <strong>{formatQuantity(orders.total)}</strong>
        <span className="report-products__details">
          <FormattedMessage id="components.pages.reports.products.billable"/>: {formatQuantity(orders.billable)}
          {' · '}<FormattedMessage id="components.pages.reports.products.direct"/>: {formatQuantity(orders.direct_allocation)}
        </span>
        <span className="report-products__details">
          <FormattedMessage id="components.pages.reports.products.activeOrganizations"/>: {formatQuantity(row.active_organizations)}
        </span>
      </td>
      <td data-label={labels.netWithoutVat}>
        <CommercialValue basis="without_vat" commercialValue={row.commercial_value} directAllocationValue={row.direct_allocation_value} netSales={row.net_sales}/>
        {comparison && (
          <span className="report-products__comparison">
            {formatPercent(comparison.net_sales_without_vat_percent)}
          </span>
        )}
      </td>
      <td data-label={labels.netWithVat}>
        <CommercialValue basis="with_vat" commercialValue={row.commercial_value} directAllocationValue={row.direct_allocation_value} netSales={row.net_sales}/>
      </td>
      <td data-label={labels.vatRefunds}>
        <strong>{formatMinorUnits(row.net_sales, 'vat')}</strong>
        <span className="report-products__details">
          <FormattedMessage id="components.pages.reports.products.refunds"/>: {formatMinorUnits(row.refunds, 'with_vat')}
        </span>
      </td>
    </tr>
  );
}

function groupProductRows(rows, locale, formatMessage) {
  const groups = new Map();

  rows.forEach((row, index) => {
    const productGroup = row.product_group;
    const section = resolveProductReportSection(productGroup, locale, formatMessage);
    let group = groups.get(section.key);

    if (!group) {
      group = {
        identity: section.key,
        key: section.key,
        label: section.label
          || formatMessage({ id: 'components.pages.reports.products.ungrouped' }),
        rank: section.rank,
        firstIndex: index,
        rows: []
      };
      groups.set(section.key, group);
    }

    group.rows.push(row);
  });

  return Array.from(groups.values()).sort((left, right) => (
    left.rank - right.rank || left.firstIndex - right.firstIndex
  ));
}

function ProductsTable({ data, table, onTableChange }) {
  const intl = useIntl();
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const rowGroups = groupProductRows(rows, intl.locale, intl.formatMessage);
  const pagination = (data.meta && data.meta.pagination) || {};
  const currentPage = Number(pagination.page || table.page);
  const lastPage = Math.max(1, Number(pagination.last_page || 1));
  const total = Number(pagination.total || 0);
  const perPage = Number(pagination.per_page || table.perPage);
  const firstResult = total === 0 ? 0 : ((currentPage - 1) * perPage) + 1;
  const lastResult = Math.min(total, currentPage * perPage);
  const labels = {
    product: intl.formatMessage({ id: 'components.pages.reports.products.product' }),
    quantity: intl.formatMessage({ id: 'components.pages.reports.products.quantity' }),
    orders: intl.formatMessage({ id: 'components.pages.reports.products.orders' }),
    netWithoutVat: intl.formatMessage({ id: 'components.pages.reports.products.netWithoutVat' }),
    netWithVat: intl.formatMessage({ id: 'components.pages.reports.products.netWithVat' }),
    vatRefunds: intl.formatMessage({ id: 'components.pages.reports.products.vatRefunds' })
  };

  const pageTo = page => onTableChange({ ...table, page });

  return (
    <section aria-label={intl.formatMessage({ id: 'components.pages.reports.products.tableLabel' })} className="report-products">
      <p className="sr-only" id="product-report-sort-scope">
        <FormattedMessage id="components.pages.reports.products.sortScope"/>
      </p>
      <div className="report-products__table-wrap">
        <table aria-describedby="product-report-sort-scope" className="report-products__table">
          <colgroup>
            <col className="report-products__col-product"/>
            <col className="report-products__col-quantity"/>
            <col className="report-products__col-orders"/>
            <col className="report-products__col-money"/>
            <col className="report-products__col-money"/>
            <col className="report-products__col-vat"/>
          </colgroup>
          <thead>
            <tr>
              {['name', 'fulfilled_quantity', 'orders'].map(column => (
                <th key={column} scope="col">
                  <ProductSortButton column={column} onChange={onTableChange} table={table}/>
                </th>
              ))}
              {['net_sales_without_vat', 'net_sales_with_vat'].map(column => (
                <th key={column} scope="col">
                  <ProductSortButton column={column} onChange={onTableChange} table={table}/>
                </th>
              ))}
              <th scope="col">{labels.vatRefunds}</th>
            </tr>
          </thead>
          {rowGroups.map(group => (
            <tbody className="report-products__group" data-product-section-id={group.identity} key={group.key}>
              <tr className="report-products__group-row" data-testid="product-report-group">
                <th colSpan="6" scope="rowgroup">
                  <i
                    aria-hidden="true"
                    className="report-products__group-swatch"
                    style={{ '--report-series-color': productSectionColor(group.identity) }}
                  />
                  <span title={group.label}>{group.label}</span>
                </th>
              </tr>
              {group.rows.map(row => (
                <ProductRow key={row.uuid || row.id} labels={labels} row={row}/>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      <footer className="report-products__footer">
        <label className="report-products__rows">
          <FormattedMessage id="components.pages.reports.products.groupsPerPage"/>
          <select
            aria-label={intl.formatMessage({ id: 'components.pages.reports.products.groupsPerPage' })}
            onChange={event => onTableChange({ ...table, page: 1, perPage: Number(event.target.value) })}
            value={table.perPage}
          >
            {[10, 25, 50, 100].map(value => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <span aria-live="polite" className="report-products__range">
          <FormattedMessage
            id="components.pages.reports.products.groupRange"
            values={{ first: firstResult, last: lastResult, total }}
          />
        </span>
        <nav aria-label={intl.formatMessage({ id: 'components.pages.reports.products.groupPagination' })} className="report-products__pagination">
          <button aria-label={intl.formatMessage({ id: 'components.pages.reports.products.firstPage' })} disabled={currentPage <= 1} onClick={() => pageTo(1)} type="button">
            <i aria-hidden="true" className="fas fa-angle-double-left"/>
          </button>
          <button aria-label={intl.formatMessage({ id: 'components.pages.reports.products.previousPage' })} disabled={currentPage <= 1} onClick={() => pageTo(currentPage - 1)} type="button">
            <i aria-hidden="true" className="fas fa-angle-left"/>
          </button>
          <span>{currentPage} / {lastPage}</span>
          <button aria-label={intl.formatMessage({ id: 'components.pages.reports.products.nextPage' })} disabled={currentPage >= lastPage} onClick={() => pageTo(currentPage + 1)} type="button">
            <i aria-hidden="true" className="fas fa-angle-right"/>
          </button>
          <button aria-label={intl.formatMessage({ id: 'components.pages.reports.products.lastPage' })} disabled={currentPage >= lastPage} onClick={() => pageTo(lastPage)} type="button">
            <i aria-hidden="true" className="fas fa-angle-double-right"/>
          </button>
        </nav>
      </footer>
      {data.meta && (
        <div className="report-products__freshness">
          {data.meta.indexed_at && (
            <span><FormattedMessage id="components.pages.reports.summary.updated"/>: {data.meta.indexed_at}</span>
          )}
          {Number(data.meta.fallback_payment_timestamp_count || 0) > 0 && (
            <span className="report-summary__freshness-warning">
              <FormattedMessage
                id="components.pages.reports.summary.fallbackTimestamps"
                values={{ count: data.meta.fallback_payment_timestamp_count }}
              />
            </span>
          )}
        </div>
      )}
    </section>
  );
}

function ProductsReport({ data, error, loading, onRetry, onTableChange, table }) {
  if (loading) {
    return (
      <div aria-live="polite" className="report-products-loading" data-testid="products-loading" role="status">
        <span/><span/><span/>
        <span className="sr-only"><FormattedMessage id="components.pages.reports.products.loading"/></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-state report-state--error" role="alert">
        <FormattedMessage id="components.pages.reports.products.error"/>
        <button className="btn btn-outline-primary" onClick={onRetry} type="button">
          <FormattedMessage id="components.pages.reports.products.retry"/>
        </button>
      </div>
    );
  }

  if (!data || !Array.isArray(data.rows) || data.rows.length === 0) {
    return <div className="report-state"><FormattedMessage id="components.pages.reports.products.empty"/></div>;
  }

  return <ProductsTable data={data} onTableChange={onTableChange} table={table}/>;
}

export { groupProductRows, ProductsReport, ProductsTable };
